import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Card,
  Table,
  Button,
  Dropdown,
  Spinner,
  InputGroup
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faSort,
  faEye,
  faEdit,
  faEllipsisV
} from '@fortawesome/free-solid-svg-icons'
import { DebounceInput } from 'react-debounce-input'
import { Link, useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import Pagination from '../../../../GlobalModules/Utility/Components/Pagination'
import Utility from '../../../../GlobalModules/Utility/Utility'
import * as CalendarController from '../Controller/CalendarController'

const BookingList = (props) => {
  const { lng } = useParams()
  const history = useHistory()

  const [rows, setRows] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [sortField, setSortField] = useState('start_date')
  const [sortDirection, setSortDirection] = useState('desc')
  const [loading, setLoading] = useState(true)
  const [totalItems, setTotalItems] = useState(0)
  const suppressNextFetchRef = useRef(false)

  const [filters, setFilters] = useState({
    limit: 10,
    offset: 0,
    search: ''
  })

  const performFetch = useCallback(
    async (effectiveFilters) => {
      try {
        setLoading(true)

        // Get store_id from user credentials or localStorage
        let storeId = null

        if (props.userEntryType?.user_type === 'staff') {
          if (localStorage.getItem('store_id') == null) {
            localStorage.setItem('store_id', props.userEntryType.branch_id)
          }
          storeId = localStorage.getItem('store_id')
        } else {
          // For admin users, get store_id from user credentials or use default
          storeId =
            props.userCredentials?.store_id || localStorage.getItem('store_id')
        }

        // Add store_id to filters
        const filtersWithStore = {
          ...effectiveFilters,
          store_id: storeId
        }

        console.log(
          'Fetching calendar bookings with filters:',
          filtersWithStore
        )
        const response = await CalendarController.getBookingsApi(
          filtersWithStore
        )
        if (response.success) {
          setRows(response.data || [])
          setTotalItems(response.total || 0)
        } else {
          setRows([])
          setTotalItems(0)
        }
      } catch (err) {
        setRows([])
        setTotalItems(0)
      } finally {
        setLoading(false)
      }
    },
    [props.userEntryType, props.userCredentials]
  )

  const fetchRows = useCallback(async () => {
    await performFetch(filters)
  }, [filters, performFetch])

  useEffect(() => {
    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false
      return
    }
    fetchRows()
  }, [fetchRows])

  const handleLimitChange = (limit) => {
    const newLimit = parseInt(limit)
    setItemsPerPage(newLimit)
    setCurrentPage(1)
    setFilters((prev) => ({ ...prev, limit: newLimit, offset: 0 }))
  }

  const handleSearch = (e) => {
    const raw = e.target.value || ''
    const clean = raw.replace(/\s+/g, '')
    setSearchTerm(clean)
    setCurrentPage(1)
    const next = {
      ...filters,
      search: clean.length >= 2 ? clean : '',
      offset: 0
    }
    suppressNextFetchRef.current = true
    setFilters(next)
    if (clean.length >= 2 || clean.length === 0) {
      performFetch(next)
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    setFilters((prev) => ({ ...prev, offset: (page - 1) * prev.limit }))
  }

  const handleSort = (field) => {
    let newSortDirection = 'asc'
    if (sortField === field) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      setSortField(field)
    }
    setSortDirection(newSortDirection)
  }

  const dateTime = (value) => Utility.getSafeTimeFormat(value, 'N/A')

  return (
    <Container fluid className='p-0'>
      <div className='d-flex justify-content-between align-items-center top-header-box'>
        <h4>Bookings & Events</h4>
      </div>

      <Card className='custom-border-light'>
        <Card.Body>
          <div className='d-flex align-items-center gap-2 mb-3'>
            <div className='d-flex align-items-center gap-2 ms-auto'>
              <span>Show</span>
              <select
                className='form-select form-select-sm'
                style={{ width: 80 }}
                value={itemsPerPage}
                onChange={(e) => handleLimitChange(e.target.value)}
              >
                {[10, 25, 50, 100, 500, 1000].map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <span>entries</span>
            </div>
            <div className='ms-2' style={{ width: 260 }}>
              <InputGroup size='sm'>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <DebounceInput
                  minLength={1}
                  debounceTimeout={500}
                  className='form-control'
                  placeholder='Search...'
                  value={searchTerm || ''}
                  onChange={(e) => handleSearch(e)}
                  onKeyDown={(e) => {
                    if (e.key === ' ') e.preventDefault()
                  }}
                />
              </InputGroup>
            </div>
          </div>

          <div className='table-responsive'>
            <Table hover className='align-middle'>
              <thead>
                <tr>
                  <th>Event Type</th>
                  <th>Event Code</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Contact No</th>
                  <th>Created On</th>
                  <th>Updated On</th>
                  <th>Created By</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan='11' className='text-center py-4'>
                      <Spinner animation='border' variant='primary' />
                      <span className='ms-2'>Loading bookings...</span>
                    </td>
                  </tr>
                ) : rows.length > 0 ? (
                  rows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.event_type}</td>
                      <td>{r.event_code}</td>
                      <td>{dateTime(r.start_date)}</td>
                      <td>{dateTime(r.end_date)}</td>
                      <td>{r.name}</td>
                      <td>{r.email}</td>
                      <td>{r.phone}</td>
                      <td>{dateTime(r.created_at)}</td>
                      <td>{dateTime(r.updated_at)}</td>
                      <td>{r.created_by}</td>
                      <td>
                        <Dropdown align='end'>
                          <Dropdown.Toggle
                            variant='light'
                            size='sm'
                            id={`dropdown-${r.id}`}
                            className='btn-icon no-caret'
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              as={Link}
                              to={`/${lng}/calendar/booking/view/${r.slack}`}
                            >
                              <FontAwesomeIcon icon={faEye} className='me-2' />
                              View
                            </Dropdown.Item>
                            <Dropdown.Item
                              as={Link}
                              to={`/${lng}/calendar/booking/edit/${r.slack}`}
                            >
                              <FontAwesomeIcon icon={faEdit} className='me-2' />
                              Edit
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan='11' className='text-center py-4'>
                      {searchTerm
                        ? 'No bookings found for your search.'
                        : 'No bookings found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

           <div className='paginationSec'>
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              siblingCount={2}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 25, 50, 100, 500, 1000]}
              showSummary={true}
              showLimit={false}
            />
          </div>
        </Card.Body>
      </Card>
    </Container>
  )
}

const mapStateToProps = (globalState) => ({
  userCredentials: globalState.LoginReducer.userCredentials,
  token: globalState.LoginReducer.token,
  userEntryType: globalState.LoginReducer.userEntryType
})

export default connect(mapStateToProps)(BookingList)
